import { useMemo } from 'react';
import { Case, Switch, When } from 'react-if';
import { ErrorBoundary } from '@toss/error-boundary';
import { usePreservedCallback } from '@toss/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { ExportIcon } from '@zep/icons';
import { Currency } from '@zep/types';
import { Chip, Table, TableColumn } from '@zep/ui';
import { fDateByCountryCode } from '@zep/utils';
import { useGetSpacePlans } from '../../../../services';
import { OperationType, PaymentStatus, SpacePlanWithPayment } from '../../../../types/SubscriptionType';
import { fCurrency } from '../../../../utils';
import { PlanSpaceDetailProvider } from '../plan';
import { PlanSpacesLayout, PlanSpacesSection } from '../space/organisms';
import S from './SpaceBillingInfo.module.scss';
const FIXED_COLUMN_WIDTH = 90;
export const SpaceBillingInfoSection = () => {
  const router = useRouter();
  return <PlanSpaceDetailProvider spaceHashId={router.query.id as string} data-sentry-element="PlanSpaceDetailProvider" data-sentry-component="SpaceBillingInfoSection" data-sentry-source-file="SpaceBillingInfoSection.tsx">
      <PlanSpacesLayout activeType={'billing'} data-sentry-element="PlanSpacesLayout" data-sentry-source-file="SpaceBillingInfoSection.tsx">
        <ErrorBoundary renderFallback={() => null} data-sentry-element="ErrorBoundary" data-sentry-source-file="SpaceBillingInfoSection.tsx">
          <Container data-sentry-element="Container" data-sentry-source-file="SpaceBillingInfoSection.tsx" />
        </ErrorBoundary>
      </PlanSpacesLayout>
    </PlanSpaceDetailProvider>;
};
const Container = () => {
  const {
    query
  } = useRouter();
  const spaceHashIdQueryParam = query.id as string;
  const {
    data: {
      spacePlans,
      countryCode
    }
  } = useGetSpacePlans(spaceHashIdQueryParam);
  const {
    t
  } = useTranslation(['subscription']);
  const dialog = useDialog();
  const handleClickReceipt = usePreservedCallback((receiptUrl: string) => {
    if (countryCode === 'KR' && !!receiptUrl) {
      window.open(receiptUrl, '_blank');
      return;
    }
    dialog.alert({
      title: t('subscription:SpaceDetail.paypleAlert.title'),
      content: t('subscription:SpaceDetail.paypleAlert.content')
    });
  });
  const getPaymentTypeString = (spacePlanWithPayment: SpacePlanWithPayment) => {
    if (spacePlanWithPayment.spacePlan?.operationType === OperationType.정기 || spacePlanWithPayment.spacePlan?.operationType === OperationType.커스텀_정기) {
      return spacePlanWithPayment.payment.cardName;
    }
    switch (spacePlanWithPayment.payment.type) {
      case 'CARD':
        return t('subscription:SpaceDetail.menuInvoice.tagCreditCard', {
          defaultValue: '신용카드'
        }) + (spacePlanWithPayment.spacePlan?.currency === 'KRW' ? `(${spacePlanWithPayment.payment.cardNumber.slice(-4)})` : '');
      case 'TRANSFER':
      case 'MANUAL_TRANSFER':
        return t('subscription:SpaceDetail.menuInvoice.tagTransfer', {
          defaultValue: '계좌 이체'
        });
      case 'VIRTUAL_ACCOUNT':
        return t('subscription:SpaceDetail.menuInvoice.tagVirtualTransfer', {
          defaultValue: '가상 계좌'
        });
      default:
        return '';
    }
  };
  const tableColumns = useMemo(() => [{
    title: t('subscription:SpaceDetail.menuInvoice.duringDate'),
    // @ts-ignore
    render({
      spacePlan
    }) {
      const formattedStartDate = fDateByCountryCode(spacePlan.startDate, countryCode);
      const formattedEndDate = fDateByCountryCode(spacePlan.endDate, countryCode);
      return <When condition={!!spacePlan}>
              <div>{`${formattedStartDate} ~ ${formattedEndDate}`}</div>
            </When>;
    }
  }, {
    title: t('subscription:SpaceDetail.menuInvoice.columnPlan'),
    // @ts-ignore
    render: ({
      spacePlan
    }) => {
      return <When condition={!!spacePlan}>
              <div>{`${spacePlan.name} ${spacePlan.maxCcu < 0 ? t('subscription:pricing.calculation.barUnlimited') : spacePlan.maxCcu}`}</div>
            </When>;
    }
  }, {
    title: t('subscription:SpaceDetail.menuInvoice.paymentMethod', {
      defaultValue: '결제 수단'
    }),
    // @ts-ignore
    render({
      payment,
      spacePlan
    }) {
      return payment && <div>{getPaymentTypeString({
          payment,
          spacePlan
        })}</div>;
    }
  }, {
    title: t('subscription:SpaceDetail.menuInvoice.columnPrice'),
    // @ts-ignore
    render({
      spacePlan,
      payment
    }) {
      return <When condition={!!spacePlan}>
              <div>
                {fCurrency(payment?.totalPrice ?? spacePlan?.salePrice, spacePlan?.currency as Currency, {
            position: 'pre'
          })}
              </div>
            </When>;
    }
  }, {
    title: t('subscription:SpaceDetail.menuInvoice.columnStatus'),
    // @ts-ignore
    render({
      payment
    }) {
      return <Switch>
              <Case condition={!payment || payment.status === PaymentStatus.WAITING_FOR_DEPOSIT}>
                <Chip variant="primary">
                  {t('subscription:SpaceDetail.menuInvoice.tagPending')}
                </Chip>
              </Case>
              <Case condition={payment?.status === PaymentStatus.SUCCESS}>
                <Chip variant="default">
                  {t('subscription:SpaceDetail.menuInvoice.tagSuccess')}
                </Chip>
              </Case>
              <Case condition={payment?.status === PaymentStatus.FAILED}>
                <Chip variant="alert">
                  {t('subscription:SpaceDetail.menuInvoice.tagFail')}
                </Chip>
              </Case>
              <Case condition={payment?.status === PaymentStatus.REFUNDED}>
                <Chip variant="alert">
                  {t('subscription:SpaceDetail.menuInvoice.tagRefunded', {
              defaultValue: '환불 완료'
            })}
                </Chip>
              </Case>
              <Case condition={payment?.status === PaymentStatus.CANCELED}>
                <Chip variant="alert">
                  {t('subscription:SpaceDetail.menuInvoice.tagPaymentCancel', {
              defaultValue: '결제 취소'
            })}
                </Chip>
              </Case>
            </Switch>;
    }
  }, {
    title: t('subscription:SpaceDetail.menu.invoice'),
    width: FIXED_COLUMN_WIDTH,
    // @ts-ignore
    render({
      payment,
      spacePlan
    }) {
      return <When condition={payment?.status === PaymentStatus.SUCCESS || payment?.status === PaymentStatus.REFUNDED}>
              <Link href={{
          pathname: `/payment/invoice/${spacePlan?.spacePlanHashId}`
        }} onClick={() => {
          logger.track('home_subscription_invoice_button');
        }} className={S.download} target={'_blank'} prefetch={false}>
                <ExportIcon width={16} height={16} />
              </Link>
            </When>;
    }
  }, {
    title: t('subscription:SpaceDetail.menuInvoice.receipt'),
    width: FIXED_COLUMN_WIDTH,
    // @ts-ignore
    render({
      payment
    }) {
      return <When condition={payment?.status === PaymentStatus.SUCCESS || payment?.status === PaymentStatus.REFUNDED}>
              <button onClick={() => handleClickReceipt(payment?.receiptUrl)} className={S.download}>
                <ExportIcon width={16} height={16} />
              </button>
            </When>;
    }
  }], [countryCode, handleClickReceipt, t]);
  return <PlanSpacesSection title={t('subscription:SpaceList.button.toBillingHistory')} data-sentry-element="PlanSpacesSection" data-sentry-component="Container" data-sentry-source-file="SpaceBillingInfoSection.tsx">
      <div className={S.full_width}>
        <Table columns={tableColumns as TableColumn<{
        [key: string]: any;
      }>[]} data={spacePlans} data-sentry-element="Table" data-sentry-source-file="SpaceBillingInfoSection.tsx" />
      </div>
    </PlanSpacesSection>;
};